import { ForwardedRef, forwardRef } from 'react';
import Link from 'next/link';
import { Box } from 'theme-ui';
import styles from '@components/Button/Button.styles';
import buttonsThemes from '@components/Button/themes';
import { TProps } from '@components/Button/typescript';
import Loading from '@components/Loading/Loading';
import { useTranslation } from 'next-i18next';
const SR_ONLY_NEW_TAB_KEY = 'accessibility.opensInNewTab';
const sharedSx = (props: {
  variant: TProps['variant'];
  variantColor: TProps['variantColor'];
  variantBackgroundColor: TProps['variantBackgroundColor'];
  size: TProps['size'];
  letterCase: TProps['letterCase'];
  fullWidth: TProps['fullWidth'];
  disabled: TProps['disabled'];
  loading: TProps['loading'];
  active: TProps['active'];
  addSx: TProps['addSx'];
}) => ({
  ...styles.shared(props.variant || 'default', props.size || 'default', props.letterCase || 'none', props.fullWidth ?? false, props.disabled ?? false, props.loading ?? false),
  ...styles.variant(props.variant || 'default', props.variantColor || 'brand', props.variantBackgroundColor, props.size || 'default', props.active ?? false),
  ...(props.addSx ?? {})
}) as Record<string, unknown>;
const Button = forwardRef(({
  variant = 'default',
  variantColor = 'brand',
  variantBackgroundColor = undefined,
  disabled = false,
  name = '',
  size = 'default',
  addSx = {},
  letterCase = 'none',
  fullWidth = false,
  loading = false,
  type = 'button',
  cy = '',
  onClick = () => undefined,
  children,
  active = false,
  addClassName = '',
  title = '',
  href,
  openInNewTab = false,
  target,
  rel
}: TProps, ref?: ForwardedRef<HTMLButtonElement | HTMLAnchorElement>) => {
  const {
    t
  } = useTranslation('common');
  const isLink = typeof href === 'string' && href.length > 0;
  const linkTarget = openInNewTab ? '_blank' : target;
  const linkRel = openInNewTab ? 'noopener noreferrer' : rel;
  const sx = sharedSx({
    variant,
    variantColor,
    variantBackgroundColor,
    size,
    letterCase,
    fullWidth,
    disabled,
    loading,
    active,
    addSx
  });
  const testId = cy || 'button';
  if (isLink) {
    return <Link href={href} ref={ref as ForwardedRef<HTMLAnchorElement>} target={linkTarget} rel={linkRel} className={addClassName} data-testid={testId} onClick={e => {
      if (disabled) {
        e.preventDefault();
        return;
      }
      onClick(e as React.MouseEvent<HTMLAnchorElement>);
      (e.currentTarget as HTMLAnchorElement).blur();
    }} aria-disabled={disabled} aria-label={title || undefined} title={title || undefined} style={{
      textDecoration: 'none',
      display: fullWidth ? 'flex' : 'inline-flex',
      width: fullWidth ? '100%' : undefined,
      minWidth: fullWidth ? 0 : undefined
    }}>
          <Box as="span" sx={{
        ...sx,
        ...(disabled ? {
          cursor: 'not-allowed',
          pointerEvents: 'none' as const
        } : {})
      }} style={{
        width: '100%',
        alignItems: 'center',
        justifyContent: 'center',
        display: 'inline-flex',
        minWidth: 0
      }} data-testid={`${testId}-inner`}>
            {variant && variantColor && loading && <span sx={styles.loadingWrapper(size)} data-testid={`${testId}-loading`}>
                <Loading color={buttonsThemes.loading[variant][variantColor].color} />
              </span>}
            <span className="button-children-wrapper" sx={styles.childrenWrapper(loading)} data-testid={`${testId}-children`}>
              {children}
              {linkTarget === '_blank' && <span className="sr-only" data-testid={`${testId}-new-tab-label`}>
                  {t(SR_ONLY_NEW_TAB_KEY)}
                </span>}
            </span>
          </Box>
        </Link>;
  }
  return <button ref={ref as ForwardedRef<HTMLButtonElement>} type={type || 'button'} name={name} data-testid={testId} onClick={e => {
    onClick(e);
    e.currentTarget.blur();
  }} className={addClassName} title={title} aria-label={title || undefined} sx={sx} disabled={disabled}>
        {variant && variantColor && loading && <span sx={styles.loadingWrapper(size)} data-testid={`${testId}-loading`}>
            <Loading color={buttonsThemes.loading[variant][variantColor].color} />
          </span>}
        <span className="button-children-wrapper" sx={styles.childrenWrapper(loading)} data-testid={`${testId}-children`}>
          {children}
        </span>
      </button>;
});
Button.displayName = 'Button';
export default Button;